// Master Integration Registry
// Single source of truth for all integration metadata across the app.
// Both Integrations.tsx (settings page) and SelectIntegrations.tsx (onboarding)
// derive their data from this registry.

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Sidebar filter category used on the Integrations settings page. */
export type PageCategory =
  | "development"
  | "communication"
  | "project-management"
  | "cloud"
  | "analytics"
  | "crm"
  | "productivity";

/** Category grouping used on the SelectIntegrations onboarding page. */
export type OnboardingCategory =
  | "banking"
  | "revenue"
  | "engineering"
  | "product_analytics"
  | "crm_sales"
  | "communication"
  | "productivity";

export interface OnboardingCategoryInfo {
  id: OnboardingCategory;
  title: string;
  description: string;
  icon: string;
}

export interface IntegrationConfig {
  // Identity
  id: string;
  name: string;
  description: string;

  // Display
  icon: string;
  domain?: string;
  color: string;
  features: string[];
  invertIcon?: boolean;

  // Categorization
  category: PageCategory;
  onboardingCategory?: OnboardingCategory;

  // Connection source — which backend layer handles this provider
  source?: "nango" | "composio" | "custom_oauth";

  // Connection behaviour
  authType: "oauth" | "api-key";
  connectable: boolean;
  comingSoon?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  requiresApiKey?: boolean;
  requiresDataSelection?: boolean;
  beta?: boolean;

  // Docs
  docsUrl: string;
}

// ---------------------------------------------------------------------------
// Onboarding category display info
// ---------------------------------------------------------------------------

export const ONBOARDING_CATEGORIES: OnboardingCategoryInfo[] = [
  {
    id: "banking",
    title: "Banking & Revenue",
    description: "Connect banking and payment tools to track financial metrics",
    icon: "\u{1F4B0}",
  },
  {
    id: "engineering",
    title: "Engineering & Build",
    description:
      "Monitor development velocity, code commits, and engineering output",
    icon: "\u26A1",
  },
  {
    id: "product_analytics",
    title: "Product Analytics",
    description: "Track user engagement, DAU/MAU, and product metrics",
    icon: "\u{1F4CA}",
  },
  {
    id: "crm_sales",
    title: "CRM & Sales",
    description:
      "Monitor sales pipeline, deal flow, and customer relationships",
    icon: "\u{1F91D}",
  },
  {
    id: "communication",
    title: "Communication",
    description: "Integrate team communication and support channels",
    icon: "\u{1F4AC}",
  },
  {
    id: "productivity",
    title: "Productivity",
    description: "Connect calendar and file storage tools",
    icon: "\u{1F4C5}",
  },
];

// ---------------------------------------------------------------------------
// Integration Registry
// ---------------------------------------------------------------------------

export const INTEGRATION_REGISTRY: IntegrationConfig[] = [
  // ── Development & Version Control ────────────────────────────────────
  {
    id: "github",
    name: "GitHub",
    description:
      "Connect your GitHub repositories to track commits, pull requests, and contributors.",
    category: "development",
    onboardingCategory: "engineering",
    icon: "https://logo.clearbit.com/github.com",
    color: "#24292e",
    authType: "oauth",
    connectable: true,
    requiresDataSelection: true,
    docsUrl: "https://docs.github.com/en/rest",
    features: [
      "Commits",
      "Pull Requests",
      "Issues",
      "Contributors",
      "Repositories",
    ],
    domain: "github.com",
  },
  {
    id: "gitlab",
    name: "GitLab",
    description:
      "Track GitLab projects, merge requests, and pipeline status. Supports custom GitLab instances and enterprise deployments.",
    category: "development",
    onboardingCategory: "engineering",
    icon: "https://logo.clearbit.com/gitlab.com",
    color: "#fc6d26",
    authType: "oauth",
    source: "composio",
    connectable: true,
    requiresDataSelection: true,
    docsUrl: "https://docs.gitlab.com/ee/api/",
    features: [
      "Merge Requests",
      "Pipelines",
      "Issues",
      "Projects",
      "Custom Instances",
    ],
    domain: "gitlab.com",
  },

  // ── Project Management ───────────────────────────────────────────────
  {
    id: "jira",
    name: "Jira",
    description:
      "Sync Jira issues, sprints, and project progress for comprehensive reporting.",
    category: "project-management",
    onboardingCategory: "engineering",
    icon: "https://logo.clearbit.com/atlassian.com",
    color: "#0052cc",
    authType: "oauth",
    source: "composio",
    connectable: true,
    requiresDataSelection: true,
    docsUrl: "https://developer.atlassian.com/cloud/jira/platform/rest/v3/",
    features: ["Issues", "Sprints", "Projects", "Velocity", "Cycle Time"],
    domain: "atlassian.com",
  },
  {
    id: "linear",
    name: "Linear",
    description: "Track Linear issues, projects, and development cycles.",
    category: "project-management",
    onboardingCategory: "engineering",
    icon: "https://logo.clearbit.com/linear.app",
    color: "#5e6ad2",
    authType: "oauth",
    source: "composio",
    connectable: true,
    requiresDataSelection: true,
    docsUrl: "https://developers.linear.app/docs",
    features: ["Issues", "Projects", "Cycles", "Teams"],
    domain: "linear.app",
  },
  {
    id: "notion",
    name: "Notion",
    description:
      "Connect Notion workspaces to sync pages, databases, and team knowledge bases.",
    category: "project-management",
    onboardingCategory: "engineering",
    icon: "https://logo.clearbit.com/notion.so",
    color: "#000000",
    authType: "oauth",
    source: "composio",
    connectable: true,
    docsUrl: "https://developers.notion.com/",
    features: ["Pages", "Databases", "Workspaces", "Team Wiki"],
    domain: "notion.so",
  },
  {
    id: "asana",
    name: "Asana",
    description:
      "Project management and team collaboration platform. Track tasks, projects, and team workload.",
    category: "project-management",
    onboardingCategory: "engineering",
    icon: "https://logo.clearbit.com/asana.com",
    color: "#F06A6A",
    authType: "oauth",
    source: "composio",
    connectable: true,
    comingSoon: false,
    docsUrl: "https://developers.asana.com/docs",
    features: ["Tasks", "Projects", "Workspaces", "Teams", "Timelines"],
    domain: "asana.com",
  },
  {
    id: "trello",
    name: "Trello",
    description:
      "Visual project management with boards, lists, and cards for organizing work.",
    category: "project-management",
    onboardingCategory: "engineering",
    icon: "https://logo.clearbit.com/trello.com",
    color: "#0079BF",
    authType: "oauth",
    source: "composio",
    connectable: true,
    docsUrl: "https://developer.atlassian.com/cloud/trello/",
    features: ["Boards", "Lists", "Cards", "Checklists", "Activity"],
    domain: "trello.com",
  },
  {
    id: "monday",
    name: "Monday.com",
    description:
      "Work management platform for teams. Track projects, workflows, and team activities.",
    category: "project-management",
    onboardingCategory: "engineering",
    icon: "https://logo.clearbit.com/monday.com",
    color: "#FF3D57",
    authType: "oauth",
    source: "composio",
    connectable: true,
    comingSoon: false,
    docsUrl: "https://developer.monday.com/",
    features: ["Boards", "Items", "Workflows", "Dashboards"],
    domain: "monday.com",
  },
  {
    id: "clickup",
    name: "ClickUp",
    description:
      "All-in-one project management. Manage tasks, docs, goals, and time tracking.",
    category: "project-management",
    onboardingCategory: "engineering",
    icon: "https://logo.clearbit.com/clickup.com",
    color: "#7B68EE",
    authType: "oauth",
    source: "composio",
    connectable: true,
    requiresDataSelection: true,
    docsUrl: "https://clickup.com/api/",
    features: ["Tasks", "Spaces", "Goals", "Time Tracking"],
    domain: "clickup.com",
  },
  {
    id: "confluence",
    name: "Confluence",
    description:
      "Team workspace for documentation, knowledge sharing, and collaboration.",
    category: "project-management",
    onboardingCategory: "engineering",
    icon: "https://logo.clearbit.com/atlassian.com",
    color: "#172B4D",
    authType: "oauth",
    source: "composio",
    connectable: true,
    requiresDataSelection: true,
    docsUrl: "https://developer.atlassian.com/cloud/confluence/",
    features: ["Pages", "Spaces", "Comments", "Templates"],
    domain: "atlassian.com",
  },

  // ── Communication ────────────────────────────────────────────────────
  {
    id: "slack",
    name: "Slack",
    description: "Monitor Slack channels, messages, and team engagement.",
    category: "communication",
    onboardingCategory: "communication",
    icon: "https://logo.clearbit.com/slack.com",
    color: "#4a154b",
    authType: "oauth",
    connectable: true,
    requiresDataSelection: true,
    docsUrl: "https://api.slack.com/",
    features: ["Messages", "Channels", "Users", "Team Activity"],
    domain: "slack.com",
  },
  {
    id: "intercom",
    name: "Intercom",
    description:
      "Connect Intercom to sync customer conversations, support tickets, and user engagement data.",
    category: "communication",
    onboardingCategory: "communication",
    icon: "https://logo.clearbit.com/intercom.com",
    color: "#1f8ded",
    authType: "oauth",
    source: "composio",
    connectable: true,
    requiresDataSelection: true,
    docsUrl: "https://developers.intercom.com/docs/references/rest-api/",
    features: [
      "Conversations",
      "Users",
      "Companies",
      "Tags",
      "Segments",
      "Support Tickets",
    ],
    domain: "intercom.com",
  },
  {
    id: "zendesk",
    name: "Zendesk",
    description:
      "Sync Zendesk support tickets, customer data, and help desk metrics.",
    category: "communication",
    onboardingCategory: "communication",
    icon: "https://logo.clearbit.com/zendesk.com",
    color: "#03363d",
    authType: "oauth",
    source: "composio",
    connectable: true,
    requiresDataSelection: true,
    docsUrl: "https://developer.zendesk.com/api-reference/",
    features: [
      "Tickets",
      "Users",
      "Organizations",
      "Groups",
      "Satisfaction Ratings",
    ],
    domain: "zendesk.com",
  },
  {
    id: "gmail",
    name: "Gmail",
    description:
      "Connect Gmail to track email activity, threads, and communication patterns.",
    category: "communication",
    onboardingCategory: "communication",
    icon: "/assets/integrations/gmail.svg",
    color: "#ea4335",
    authType: "oauth",
    source: "composio",
    connectable: true,
    requiresDataSelection: true,
    docsUrl: "https://developers.google.com/gmail/api",
    features: ["Emails", "Threads", "Labels", "Contacts"],
    domain: "gmail.com",
  },
  {
    id: "mailchimp",
    name: "Mailchimp",
    description:
      "Email marketing platform. Track campaigns, audience engagement, and email performance.",
    category: "communication",
    onboardingCategory: "communication",
    icon: "https://logo.clearbit.com/mailchimp.com",
    color: "#FFE01B",
    authType: "oauth",
    source: "composio",
    connectable: false,
    comingSoon: true,
    docsUrl: "https://mailchimp.com/developer/",
    features: [
      "Campaigns",
      "Audiences",
      "Analytics",
      "Templates",
      "Automations",
    ],
    domain: "mailchimp.com",
  },
  {
    id: "microsoft-teams",
    name: "Microsoft Teams",
    description:
      "Team collaboration platform with chat, meetings, and file sharing.",
    category: "communication",
    onboardingCategory: "communication",
    icon: "https://logo.clearbit.com/microsoft.com",
    color: "#6264A7",
    authType: "oauth",
    source: "composio",
    connectable: true,
    comingSoon: false,
    docsUrl:
      "https://learn.microsoft.com/en-us/graph/teams-concept-overview",
    features: ["Channels", "Messages", "Meetings", "Files"],
    domain: "microsoft.com",
  },
  {
    id: "twilio",
    name: "Twilio",
    description:
      "Cloud communications platform for SMS, voice, and messaging.",
    category: "communication",
    onboardingCategory: "communication",
    icon: "https://logo.clearbit.com/twilio.com",
    color: "#F22F46",
    authType: "oauth",
    connectable: false,
    comingSoon: true,
    docsUrl: "https://www.twilio.com/docs/",
    features: ["SMS", "Voice", "Messaging", "Usage Analytics"],
    domain: "twilio.com",
  },
  {
    id: "sendgrid",
    name: "SendGrid",
    description:
      "Email delivery and marketing platform. Track email sends, engagement, and deliverability.",
    category: "communication",
    onboardingCategory: "communication",
    icon: "https://logo.clearbit.com/sendgrid.com",
    color: "#1A82E2",
    authType: "api-key",
    connectable: false,
    comingSoon: true,
    docsUrl: "https://docs.sendgrid.com/",
    features: ["Email Delivery", "Templates", "Analytics", "Engagement"],
    domain: "sendgrid.com",
  },
  {
    id: "mailgun-integration",
    name: "Mailgun",
    description:
      "Email API service for sending, receiving, and tracking emails.",
    category: "communication",
    onboardingCategory: "communication",
    icon: "https://logo.clearbit.com/mailgun.com",
    color: "#D9453D",
    authType: "api-key",
    connectable: false,
    comingSoon: true,
    docsUrl: "https://documentation.mailgun.com/",
    features: ["Email Delivery", "Tracking", "Analytics", "Logs"],
    domain: "mailgun.com",
  },
  {
    id: "beehiiv",
    name: "Beehiiv",
    description:
      "Newsletter platform for creators. Track subscribers, engagement, and growth.",
    category: "communication",
    onboardingCategory: "communication",
    icon: "https://logo.clearbit.com/beehiiv.com",
    color: "#FFC724",
    authType: "api-key",
    connectable: false,
    comingSoon: true,
    docsUrl: "https://docs.beehiiv.com/",
    features: ["Newsletters", "Subscribers", "Analytics", "Growth"],
    domain: "beehiiv.com",
  },

  // ── Analytics & Finance ──────────────────────────────────────────────
  {
    id: "google-sheets",
    name: "Google Sheets",
    description:
      "Connect Google Sheets to access spreadsheet data, KPIs, and business metrics.",
    category: "analytics",
    onboardingCategory: "productivity",
    icon: "https://logo.clearbit.com/google.com",
    color: "#34a853",
    authType: "oauth",
    source: "composio",
    connectable: true,
    requiresDataSelection: true,
    docsUrl: "https://developers.google.com/sheets/api",
    features: ["Spreadsheets", "KPIs", "Data Analysis", "Metrics", "Charts"],
    domain: "google.com",
  },
  {
    id: "stripe",
    name: "Stripe",
    description:
      "Connect Stripe for revenue data, customer metrics, and subscription analytics.",
    category: "analytics",
    onboardingCategory: "banking",
    icon: "https://logo.clearbit.com/stripe.com",
    color: "#635bff",
    authType: "oauth",
    source: "composio",
    connectable: true,
    comingSoon: false,
    docsUrl: "https://stripe.com/docs/api",
    features: ["Revenue", "Subscriptions", "Customers", "MRR", "Invoices"],
    domain: "stripe.com",
  },
  {
    id: "mercury",
    name: "Mercury",
    description: "Banking for startups.",
    category: "analytics",
    onboardingCategory: "banking",
    icon: "/assets/integrations/mercury.svg",
    color: "#000000",
    authType: "oauth",
    connectable: false,
    comingSoon: true,
    invertIcon: true,
    docsUrl: "https://mercury.com/docs",
    features: ["Banking", "Transactions"],
    domain: "mercury.com",
  },
  {
    id: "ramp",
    name: "Ramp",
    description: "Corporate cards and spend management.",
    category: "analytics",
    onboardingCategory: "banking",
    icon: "/assets/integrations/ramp.svg",
    color: "#000000",
    authType: "oauth",
    connectable: false,
    comingSoon: true,
    hidden: true,
    invertIcon: true,
    docsUrl: "https://ramp.com/docs",
    features: ["Spend Management", "Cards"],
    domain: "ramp.com",
  },
  {
    id: "svb",
    name: "SVB",
    description: "Silicon Valley Bank account integration.",
    category: "analytics",
    onboardingCategory: "banking",
    icon: "/assets/integrations/svb.jpeg",
    color: "#000000",
    authType: "oauth",
    connectable: false,
    comingSoon: true,
    hidden: true,
    docsUrl: "https://svb.com",
    features: ["Banking", "Transactions"],
    domain: "svb.com",
  },
  {
    id: "chargebee",
    name: "Chargebee",
    description: "Subscription billing and revenue management.",
    category: "analytics",
    onboardingCategory: "banking",
    icon: "/assets/integrations/chargebee.svg",
    color: "#000000",
    authType: "oauth",
    connectable: false,
    comingSoon: true,
    hidden: true,
    docsUrl: "https://chargebee.com",
    features: ["Billing", "Subscriptions"],
    domain: "chargebee.com",
  },
  {
    id: "recurly",
    name: "Recurly",
    description: "Subscription management and recurring billing.",
    category: "analytics",
    onboardingCategory: "banking",
    icon: "/assets/integrations/recurly.jpeg",
    color: "#000000",
    authType: "oauth",
    connectable: false,
    comingSoon: true,
    hidden: true,
    docsUrl: "https://recurly.com",
    features: ["Billing", "Subscriptions"],
    domain: "recurly.com",
  },
  {
    id: "plaid",
    name: "Plaid",
    description: "Connect bank accounts to your app.",
    category: "analytics",
    onboardingCategory: "banking",
    icon: "/assets/integrations/plaid.jpeg",
    color: "#000000",
    authType: "oauth",
    connectable: false,
    comingSoon: true,
    disabled: true,
    hidden: true,
    docsUrl: "https://plaid.com",
    features: ["Banking", "Transactions"],
    domain: "plaid.com",
  },
  {
    id: "posthog",
    name: "PostHog",
    description:
      "Open-source product analytics. Track events, feature flags, session recordings, and user behavior.",
    category: "analytics",
    onboardingCategory: "product_analytics",
    icon: "https://logo.clearbit.com/posthog.com",
    color: "#1d4aff",
    authType: "oauth",
    source: "composio",
    connectable: true,
    requiresApiKey: true,
    docsUrl: "https://posthog.com/docs/api",
    features: ["Events", "Feature Flags", "Session Recordings", "Funnels"],
    domain: "posthog.com",
  },
  {
    id: "amplitude",
    name: "Amplitude",
    description: "Product analytics and event tracking.",
    category: "analytics",
    onboardingCategory: "product_analytics",
    icon: "/assets/integrations/amplitude.svg",
    color: "#000000",
    authType: "oauth",
    source: "composio",
    connectable: true,
    requiresApiKey: true,
    invertIcon: true,
    docsUrl: "https://amplitude.com",
    features: ["Analytics", "Events"],
    domain: "amplitude.com",
  },
  {
    id: "mixpanel",
    name: "Mixpanel",
    description:
      "Product analytics for tracking user interactions, funnels, and retention metrics.",
    category: "analytics",
    onboardingCategory: "product_analytics",
    icon: "https://logo.clearbit.com/mixpanel.com",
    color: "#7856ff",
    authType: "oauth",
    source: "composio",
    connectable: true,
    requiresApiKey: true,
    docsUrl: "https://developer.mixpanel.com/docs",
    features: ["Events", "Funnels", "Retention", "User Profiles"],
    domain: "mixpanel.com",
  },
  {
    id: "segment",
    name: "Segment",
    description: "Customer data platform.",
    category: "analytics",
    onboardingCategory: "product_analytics",
    icon: "/assets/integrations/segment.png",
    color: "#000000",
    authType: "oauth",
    source: "composio",
    connectable: true,
    requiresApiKey: true,
    docsUrl: "https://segment.com",
    features: ["Data", "Events"],
    domain: "segment.com",
  },
  {
    id: "heap",
    name: "Heap",
    description: "Digital insights and analytics.",
    category: "analytics",
    onboardingCategory: "product_analytics",
    icon: "/assets/integrations/heap-white.svg",
    color: "#000000",
    authType: "oauth",
    source: "composio",
    connectable: true,
    requiresApiKey: true,
    docsUrl: "https://heap.io",
    features: ["Analytics", "Events"],
    domain: "heap.io",
  },
  {
    id: "google-analytics",
    name: "Google Analytics",
    description:
      "Web analytics for tracking website traffic, user behavior, and conversion data.",
    category: "analytics",
    onboardingCategory: "product_analytics",
    icon: "https://logo.clearbit.com/google.com",
    color: "#E37400",
    authType: "oauth",
    source: "composio",
    connectable: true,
    requiresDataSelection: true,
    docsUrl: "https://developers.google.com/analytics/",
    features: ["Traffic", "User Behavior", "Conversions", "Reports"],
    domain: "analytics.google.com",
  },
  {
    id: "google-ads",
    name: "Google Ads",
    description:
      "Online advertising platform. Track ad campaigns, spending, and performance metrics.",
    category: "analytics",
    onboardingCategory: "product_analytics",
    icon: "https://logo.clearbit.com/google.com",
    color: "#4285F4",
    authType: "oauth",
    source: "composio",
    connectable: true,
    comingSoon: false,
    docsUrl: "https://developers.google.com/google-ads/",
    features: ["Campaigns", "Ad Spend", "Conversions", "Performance"],
    domain: "ads.google.com",
  },
  {
    id: "paypal",
    name: "PayPal",
    description:
      "Payment processing platform. Track transactions, balances, and payment activity.",
    category: "analytics",
    onboardingCategory: "banking",
    icon: "https://logo.clearbit.com/paypal.com",
    color: "#003087",
    authType: "oauth",
    connectable: false,
    comingSoon: true,
    docsUrl: "https://developer.paypal.com/",
    features: ["Transactions", "Balances", "Invoicing", "Payments"],
    domain: "paypal.com",
  },
  {
    id: "shopify",
    name: "Shopify",
    description:
      "E-commerce platform. Track orders, products, customers, and sales data.",
    category: "analytics",
    onboardingCategory: "banking",
    icon: "https://logo.clearbit.com/shopify.com",
    color: "#96BF48",
    authType: "oauth",
    connectable: false,
    comingSoon: true,
    docsUrl: "https://shopify.dev/docs/",
    features: ["Orders", "Products", "Customers", "Sales Analytics"],
    domain: "shopify.com",
  },
  {
    id: "braintree",
    name: "Braintree",
    description:
      "Payment processing platform by PayPal. Handle transactions, subscriptions, and payouts.",
    category: "analytics",
    onboardingCategory: "banking",
    icon: "https://logo.clearbit.com/braintreepayments.com",
    color: "#000000",
    authType: "oauth",
    connectable: false,
    comingSoon: true,
    docsUrl: "https://developer.paypal.com/braintree/docs/",
    features: ["Transactions", "Subscriptions", "Payouts", "Disputes"],
    domain: "braintreepayments.com",
  },
  {
    id: "expensify",
    name: "Expensify",
    description: "Expense management and reporting platform.",
    category: "analytics",
    onboardingCategory: "banking",
    icon: "https://logo.clearbit.com/expensify.com",
    color: "#2F9E3E",
    authType: "oauth",
    connectable: false,
    comingSoon: true,
    docsUrl: "https://integrations.expensify.com/Integration-Server/doc/",
    features: ["Expense Reports", "Receipts", "Approvals", "Policies"],
    domain: "expensify.com",
  },
  {
    id: "gusto",
    name: "Gusto",
    description:
      "Payroll, benefits, and HR platform for growing businesses.",
    category: "analytics",
    onboardingCategory: "banking",
    icon: "https://logo.clearbit.com/gusto.com",
    color: "#F45D48",
    authType: "oauth",
    connectable: false,
    comingSoon: true,
    docsUrl: "https://docs.gusto.com/",
    features: ["Payroll", "Benefits", "HR", "Time Tracking"],
    domain: "gusto.com",
  },
  {
    id: "rippling",
    name: "Rippling",
    description:
      "Unified HR, IT, and finance platform for managing workforce operations.",
    category: "analytics",
    onboardingCategory: "banking",
    icon: "https://logo.clearbit.com/rippling.com",
    color: "#FDB515",
    authType: "oauth",
    connectable: false,
    comingSoon: true,
    docsUrl: "https://developer.rippling.com/",
    features: ["Payroll", "Benefits", "IT Management", "Finance"],
    domain: "rippling.com",
  },
  {
    id: "deel",
    name: "Deel",
    description:
      "Global payroll and compliance platform for remote teams.",
    category: "analytics",
    onboardingCategory: "banking",
    icon: "https://logo.clearbit.com/deel.com",
    color: "#15357A",
    authType: "oauth",
    connectable: false,
    comingSoon: true,
    docsUrl: "https://developer.deel.com/",
    features: ["Contracts", "Invoices", "Payroll", "Compliance"],
    domain: "deel.com",
  },

  // ── CRM & Sales ──────────────────────────────────────────────────────
  {
    id: "pipedrive",
    name: "Pipedrive",
    description:
      "CRM and sales pipeline management. Sync contacts, deals, organizations, and activities for investor updates.",
    category: "crm",
    onboardingCategory: "crm_sales",
    icon: "/assets/integrations/pipedrive.svg",
    color: "#1A1A1A",
    authType: "oauth",
    source: "composio",
    connectable: true,
    requiresDataSelection: true,
    invertIcon: true,
    docsUrl: "https://developers.pipedrive.com/docs/api/v1",
    features: [
      "Contacts",
      "Deals",
      "Organizations",
      "Activities",
      "Pipelines",
      "Sales Metrics",
    ],
    domain: "pipedrive.com",
  },
  {
    id: "close",
    name: "Close",
    description:
      "CRM for high-growth teams. Track leads, contacts, opportunities, and sales activities for comprehensive reporting.",
    category: "crm",
    onboardingCategory: "crm_sales",
    icon: "/assets/integrations/close.jpeg",
    color: "#1C3C78",
    authType: "oauth",
    source: "composio",
    connectable: true,
    requiresDataSelection: true,
    docsUrl: "https://developer.close.com/",
    features: [
      "Leads",
      "Contacts",
      "Opportunities",
      "Activities",
      "Sales Pipeline",
      "Team Performance",
    ],
    domain: "close.com",
  },
  {
    id: "attio",
    name: "Attio",
    description:
      "The CRM for the next generation of companies. Sync people, companies, and custom data models for flexible reporting.",
    category: "crm",
    onboardingCategory: "crm_sales",
    icon: "/assets/integrations/attio.svg",
    color: "#FF5C35",
    authType: "oauth",
    source: "composio",
    connectable: true,
    docsUrl: "https://developers.attio.com/",
    features: [
      "People",
      "Companies",
      "Custom Lists",
      "Attributes",
      "Relationships",
      "Workspaces",
    ],
    domain: "attio.com",
  },
  {
    id: "hubspot",
    name: "HubSpot",
    description:
      "All-in-one CRM platform. Sync contacts, deals, companies, and marketing data for comprehensive reporting.",
    category: "crm",
    onboardingCategory: "crm_sales",
    icon: "https://logo.clearbit.com/hubspot.com",
    color: "#ff7a59",
    authType: "oauth",
    source: "composio",
    connectable: true,
    comingSoon: false,
    requiresDataSelection: true,
    docsUrl: "https://developers.hubspot.com/docs/api/overview",
    features: [
      "Contacts",
      "Deals",
      "Companies",
      "Marketing",
      "Pipelines",
      "Analytics",
    ],
    domain: "hubspot.com",
  },
  {
    id: "marketo",
    name: "Marketo",
    description:
      "Marketing automation platform for lead management and campaign analytics.",
    category: "crm",
    onboardingCategory: "crm_sales",
    icon: "https://logo.clearbit.com/marketo.com",
    color: "#5C4C9F",
    authType: "api-key",
    source: "composio",
    connectable: true,
    requiresApiKey: true,
    docsUrl: "https://developers.marketo.com/",
    features: ["Leads", "Campaigns", "Email Marketing", "Analytics"],
    domain: "marketo.com",
  },
  {
    id: "odoo",
    name: "Odoo",
    description: "Open-source ERP and business management suite.",
    category: "crm",
    onboardingCategory: "crm_sales",
    icon: "https://logo.clearbit.com/odoo.com",
    color: "#714B67",
    authType: "api-key",
    connectable: false,
    comingSoon: true,
    docsUrl: "https://www.odoo.com/documentation/",
    features: ["CRM", "Inventory", "Accounting", "Projects"],
    domain: "odoo.com",
  },

  // ── Productivity & Scheduling ────────────────────────────────────────
  {
    id: "calendly",
    name: "Calendly",
    description:
      "Scheduling automation platform. Sync scheduled events, invitees, and meeting data for tracking founder meetings.",
    category: "project-management",
    onboardingCategory: "productivity",
    icon: "/assets/integrations/calendly.svg",
    color: "#006BFF",
    authType: "oauth",
    source: "composio",
    connectable: true,
    docsUrl: "https://developer.calendly.com/",
    features: [
      "Scheduled Events",
      "Event Types",
      "Invitees",
      "Meeting Analytics",
      "Calendar Sync",
    ],
    domain: "calendly.com",
  },
  {
    id: "airtable",
    name: "Airtable",
    description:
      "Low-code database platform. Connect bases, tables, and records for flexible data tracking and reporting.",
    category: "project-management",
    onboardingCategory: "productivity",
    icon: "/assets/integrations/airtable.svg",
    color: "#18BFFF",
    authType: "oauth",
    source: "composio",
    connectable: true,
    comingSoon: false,
    docsUrl: "https://airtable.com/developers/web/api/introduction",
    features: [
      "Bases",
      "Tables",
      "Records",
      "Views",
      "Attachments",
      "Collaborators",
    ],
    domain: "airtable.com",
  },
  {
    id: "google-calendar",
    name: "Google Calendar",
    description:
      "Sync calendar events, meeting schedules, and availability for team coordination and reporting.",
    category: "project-management",
    onboardingCategory: "productivity",
    icon: "/assets/integrations/google-calendar.svg",
    color: "#4285f4",
    authType: "oauth",
    source: "composio",
    connectable: true,
    requiresDataSelection: true,
    docsUrl: "https://developers.google.com/calendar/api",
    features: ["Events", "Schedules", "Availability", "Meeting Analytics"],
    domain: "calendar.google.com",
  },
  {
    id: "google-drive",
    name: "Google Drive",
    description:
      "Connect Google Drive to access files, documents, and shared resources for team collaboration.",
    category: "project-management",
    onboardingCategory: "productivity",
    icon: "/assets/integrations/google-drive.svg",
    color: "#0f9d58",
    authType: "oauth",
    source: "composio",
    connectable: true,
    requiresDataSelection: true,
    docsUrl: "https://developers.google.com/drive/api",
    features: ["Files", "Documents", "Folders", "Shared Drives"],
    domain: "drive.google.com",
  },
  {
    id: "figma",
    name: "Figma",
    description:
      "Collaborative design tool. Access design files, projects, and team activity.",
    category: "project-management",
    onboardingCategory: "productivity",
    icon: "https://logo.clearbit.com/figma.com",
    color: "#F24E1E",
    authType: "oauth",
    source: "composio",
    connectable: true,
    requiresDataSelection: true,
    beta: true,
    docsUrl: "https://www.figma.com/developers/",
    features: ["Files", "Projects", "Comments", "Components"],
    domain: "figma.com",
  },
  {
    id: "miro",
    name: "Miro",
    description:
      "Online collaborative whiteboard for visual project management and brainstorming.",
    category: "project-management",
    onboardingCategory: "productivity",
    icon: "https://logo.clearbit.com/miro.com",
    color: "#FFD02F",
    authType: "oauth",
    source: "composio",
    connectable: false,
    comingSoon: true,
    docsUrl: "https://developers.miro.com/",
    features: ["Boards", "Sticky Notes", "Diagrams", "Collaboration"],
    domain: "miro.com",
  },
  {
    id: "docusign",
    name: "DocuSign",
    description:
      "Electronic signature and agreement management platform.",
    category: "project-management",
    onboardingCategory: "productivity",
    icon: "https://logo.clearbit.com/docusign.com",
    color: "#FFCD00",
    authType: "oauth",
    connectable: false,
    comingSoon: true,
    docsUrl: "https://developers.docusign.com/",
    features: ["Envelopes", "Signatures", "Templates", "Documents"],
    domain: "docusign.com",
  },
  {
    id: "zapier",
    name: "Zapier",
    description:
      "Automation platform connecting thousands of apps. Track workflow automations and usage.",
    category: "cloud",
    onboardingCategory: "productivity",
    icon: "https://logo.clearbit.com/zapier.com",
    color: "#FF4A00",
    authType: "oauth",
    source: "composio",
    connectable: false,
    comingSoon: true,
    docsUrl: "https://zapier.com/developer/",
    features: ["Zaps", "Automations", "Connected Apps", "Usage"],
    domain: "zapier.com",
  },

  // ── Coming Soon (onboarding-only in vcOnboarding.ts) ─────────────────
  {
    id: "brex",
    name: "Brex",
    description: "Corporate card and spend management for startups.",
    category: "analytics",
    onboardingCategory: "banking",
    icon: "https://logo.clearbit.com/brex.com",
    color: "#000000",
    authType: "oauth",
    source: "composio",
    connectable: true,
    comingSoon: false,
    docsUrl: "https://developer.brex.com/",
    features: ["Corporate Cards", "Spend Management"],
    domain: "brex.com",
  },
  {
    id: "quickbooks",
    name: "QuickBooks",
    description: "Accounting and bookkeeping for small businesses.",
    category: "analytics",
    onboardingCategory: "banking",
    icon: "https://logo.clearbit.com/quickbooks.com",
    color: "#2CA01C",
    authType: "oauth",
    connectable: false,
    comingSoon: true,
    docsUrl: "https://developer.intuit.com/",
    features: ["Accounting", "Invoicing", "Expense Tracking"],
    domain: "quickbooks.com",
  },
  {
    id: "xero",
    name: "Xero",
    description: "Cloud accounting software for small businesses.",
    category: "analytics",
    onboardingCategory: "banking",
    icon: "https://logo.clearbit.com/xero.com",
    color: "#13B5EA",
    authType: "oauth",
    connectable: false,
    comingSoon: true,
    hidden: true,
    docsUrl: "https://developer.xero.com/",
    features: ["Accounting", "Invoicing", "Bank Reconciliation"],
    domain: "xero.com",
  },
  {
    id: "paddle",
    name: "Paddle",
    description: "Revenue delivery platform for SaaS companies.",
    category: "analytics",
    onboardingCategory: "banking",
    icon: "https://logo.clearbit.com/paddle.com",
    color: "#000000",
    authType: "oauth",
    connectable: false,
    comingSoon: true,
    hidden: true,
    docsUrl: "https://developer.paddle.com/",
    features: ["Billing", "Subscriptions", "Revenue"],
    domain: "paddle.com",
  },
  {
    id: "salesforce",
    name: "Salesforce",
    description:
      "Enterprise CRM platform for managing customer relationships and sales pipelines.",
    category: "crm",
    onboardingCategory: "crm_sales",
    icon: "https://logo.clearbit.com/salesforce.com",
    color: "#00A1E0",
    authType: "oauth",
    source: "composio",
    connectable: true,
    comingSoon: false,
    docsUrl: "https://developer.salesforce.com/",
    features: ["Contacts", "Opportunities", "Leads", "Reports"],
    domain: "salesforce.com",
  },

  // Composio-powered integrations (950+) are loaded dynamically at runtime
  // via ComposioService.listToolkits(). No hardcoded entries needed here.
];

// ---------------------------------------------------------------------------
// Derived Sets (computed once at module level)
// ---------------------------------------------------------------------------

export const CONNECTABLE_IDS = new Set(
  INTEGRATION_REGISTRY.filter((i) => i.connectable).map((i) => i.id),
);

export const COMING_SOON_IDS = new Set(
  INTEGRATION_REGISTRY.filter((i) => i.comingSoon).map((i) => i.id),
);

export const API_KEY_IDS = new Set(
  INTEGRATION_REGISTRY.filter((i) => i.requiresApiKey).map((i) => i.id),
);

export const DATA_SELECTION_IDS = new Set(
  INTEGRATION_REGISTRY.filter((i) => i.requiresDataSelection).map((i) => i.id),
);

export const DISABLED_IDS = new Set(
  INTEGRATION_REGISTRY.filter((i) => i.disabled).map((i) => i.id),
);

const VISIBLE_COMING_SOON_IDS = new Set(["mercury", "brex", "stripe", "zapier", "airtable", "mailchimp", "hubspot"]);

export const HIDDEN_IDS = new Set(
  INTEGRATION_REGISTRY.filter(
    (i) => i.hidden || (i.comingSoon && !VISIBLE_COMING_SOON_IDS.has(i.id)),
  ).map((i) => i.id),
);

export const ICON_INVERT_IDS = new Set(
  INTEGRATION_REGISTRY.filter((i) => i.invertIcon).map((i) => i.id),
);

export const BETA_IDS = new Set(
  INTEGRATION_REGISTRY.filter((i) => i.beta).map((i) => i.id),
);

export const GOOGLE_IDS = new Set(
  INTEGRATION_REGISTRY.filter(
    (i) => i.id.startsWith("google-") || i.id === "gmail",
  ).map((i) => i.id),
);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const registryMap = new Map(INTEGRATION_REGISTRY.map((i) => [i.id, i]));

/** Look up a single integration by ID. */
export function getIntegration(id: string): IntegrationConfig | undefined {
  return registryMap.get(id);
}

/** IDs of integrations powered by Composio. */
export const COMPOSIO_IDS = new Set(
  INTEGRATION_REGISTRY.filter((i) => i.source === "composio").map((i) => i.id),
);

/** IDs of integrations powered by Nango (or default/unset source). */
export const NANGO_IDS = new Set(
  INTEGRATION_REGISTRY.filter((i) => !i.source || i.source === "nango").map((i) => i.id),
);

/** IDs of integrations using custom OAuth. */
export const CUSTOM_OAUTH_IDS = new Set(
  INTEGRATION_REGISTRY.filter((i) => i.source === "custom_oauth").map((i) => i.id),
);

/** Get the connection source for a provider. */
export function getConnectionSource(id: string): "nango" | "composio" | "custom_oauth" {
  return registryMap.get(id)?.source || "nango";
}
